"use client";
import fetchJson from "@/lib/fetchJson";
import { useEffect, useState, useMemo, useCallback } from "react";
import { capitalize } from "@/lib/util";
import { Button, Flex, IconButton, Text, TextField } from "@radix-ui/themes";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import * as Dialog from "@/components/CustomUiComponents/Dialog";
import ClearableSelect from "../CustomUiComponents/ClearableSelect";
import Search from "@/components/CustomUiComponents/NewSearch";
import { useAllCampaigns } from "@/services/campaign";
import { useCreatorSearch } from "@/services/creator";

interface AddBudgetProps {
  mutate: () => void;
  campaignId?: any;
  defaultCreator?: Creator;
  mobile?: boolean;
}

export default function NewAddBudget({
  mutate,
  campaignId,
  defaultCreator,
  mobile,
}: AddBudgetProps) {
  const [creatorSearchInput, setCreatorSearchInput] = useState("");
  const [creatorSearchQuery, setCreatorSearchQuery] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Only fetch creators when dialog is open and search query is provided
  const { data: creatorSearchResults } = useCreatorSearch(
    creatorSearchQuery,
    "",
    isDialogOpen
  );

  const [selectedCreator, setSelectedCreator] = useState(
    defaultCreator || null
  );
  const [selectedCampaignId, setSelectedCampaignId] = useState(
    campaignId || null
  );
  const [creatorData, setCreatorData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedPrice, setSelectedPrice] = useState([]);
  const { data: campaigns } = useAllCampaigns();

  // Debounce the search input
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setCreatorSearchQuery(creatorSearchInput);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [creatorSearchInput]);

  // Memoize creator data to prevent unnecessary re-renders
  // Include the selected creator in the list to ensure it's always available
  const creatorsData = useMemo(() => {
    const searchResults = creatorSearchResults?.creators || [];

    // If there's a selected creator and it's not in the search results, add it
    if (creatorData && !searchResults.find((c) => c.id === creatorData.id)) {
      return [creatorData, ...searchResults];
    }

    return searchResults;
  }, [creatorSearchResults, creatorData]);

  // Search handler
  const handleCreatorSearchChange = useCallback((searchQuery: string) => {
    setCreatorSearchInput(searchQuery);
  }, []);

  const handleSetCreator = async (newCreatorData) => {
    const creator = await fetchJson(`/creator/${newCreatorData.value}`, {
      method: "GET",
    });
    console.log("creatorData", creator);
    setCreatorData(creator?.creator);
  };

  useEffect(() => {
    console.log("defaultCreator", defaultCreator);
    if (defaultCreator && defaultCreator?.id) {
      handleSetCreator({ value: defaultCreator.id });
      // Add an empty platform row by default for defaultCreator
      setSelectedPrice((prev) => {
        if (prev.length === 0) {
          return [{ price: null, count: 0 }];
        }
        return prev;
      });
    }
  }, [defaultCreator]);

  const add = async () => {
    const unparsable = selectedPrice.filter(
      (p) => p.price === null || p.count < 0
    );
    if (unparsable.length > 0) {
      alert("Please fill in all the fields");
      return;
    } else {
      setLoading(true);
      for (const { price, count, customCreatorPrice } of selectedPrice) {
        console.log("ADDING", count, price, campaignId);
        await fetchJson(`/creator/budget`, {
          method: "POST",
          body: JSON.stringify({
            count: parseInt(count),
            price: price,
            campaign: selectedCampaignId,
            customCreatorPrice: customCreatorPrice
              ? parseFloat(customCreatorPrice)
              : null,
          }),
        });
        setLoading(false);
        setSelectedCampaignId(campaignId || null);
        setSelectedCreator(defaultCreator);
        setSelectedPrice([]);
        setCreatorSearchInput("");
        setCreatorSearchQuery("");
        mutate();
      }
    }
  };

  return (
    <Dialog.Root open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <Dialog.Trigger>
        {mobile ? (
          <IconButton>
            <IconPlus className="icon-accent" />
          </IconButton>
        ) : (
          <Button>
            <IconPlus className="icon-accent" />
            Add Budget
          </Button>
        )}
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Content style={{ maxWidth: "550px" }}>
          <Dialog.Title>Add Budget</Dialog.Title>
          {!campaignId && (
            <Search
              def={selectedCampaignId || null}
              data={(campaigns || []).map((i) => ({
                value: i.id,
                label: i.name,
              }))}
              onChange={(e) => {
                setSelectedCampaignId(e.value);
              }}
              prompt="Enter campaign name"
              placeholder="Search campaigns..."
              width={"100%"}
            />
          )}
          <Flex direction="column" gap="3">
            <label>
              <Text as="div" size="2" mb="1" weight="bold">
                Creator
              </Text>
              {defaultCreator ? (
                <Text size="2">{creatorData?.name}</Text>
              ) : (
                <Search
                  def={selectedCreator || null}
                  data={creatorsData.map((i) => ({
                    value: i.id,
                    label: i.name,
                  }))}
                  onChange={(e) => {
                    setSelectedCreator(e);
                    handleSetCreator(e);
                    // Add an empty platform row by default
                    setSelectedPrice((prev) => {
                      if (prev.length === 0) {
                        return [{ price: null, count: 0 }];
                      }
                      return prev;
                    });
                  }}
                  onSearchChange={handleCreatorSearchChange}
                  prompt="Type at least 2 characters to search..."
                  placeholder="Search creator..."
                  width={"100%"}
                />
              )}
            </label>
            {selectedPrice.length > 0 && (
              <Flex direction="row" gap="1" justify={"center"} align={"end"}>
                <Flex style={{ width: "40%", minWidth: "200px" }}>
                  <Text as="div" size="2" mb="1" weight="bold">
                    Platform
                  </Text>
                </Flex>
                <Flex style={{ width: "120px" }}>
                  <Text as="div" size="2" mb="1" weight="bold">
                    Number of Posts
                  </Text>
                </Flex>
                <Flex style={{ width: "150px" }}>
                  <Text as="div" size="2" mb="1" weight="bold">
                    (Optional) Custom Price per Post
                  </Text>
                </Flex>
                <Flex style={{ width: "40px" }}>
                  {/* Spacer for delete button */}
                </Flex>
              </Flex>
            )}
            {selectedPrice.map(({ price, count }, i) => {
              return (
                <Flex
                  direction="row"
                  gap="1"
                  justify={"center"}
                  align={"end"}
                  key={i}
                >
                  <Flex style={{ width: "40%", minWidth: "200px" }}>
                    <ClearableSelect
                      placeholder="Select platform..."
                      options={(creatorData?.prices || []).map((i: any) => ({
                        value: i.id,
                        label: `@${i.username} (${capitalize(i.platform)})`,
                      }))}
                      value={price}
                      onValueChange={(newPrice) => {
                        selectedPrice[i].price = newPrice;
                        setSelectedPrice([...selectedPrice]);
                      }}
                    />
                  </Flex>
                  <Flex style={{ width: "120px" }}>
                    <TextField.Root
                      value={count}
                      onChange={(e) => {
                        selectedPrice[i].count = e.target.value;
                        setSelectedPrice([...selectedPrice]);
                      }}
                      variant="soft"
                      style={{ width: "100%" }}
                    />
                  </Flex>
                  <Flex style={{ width: "150px" }}>
                    <TextField.Root
                      value={selectedPrice[i].customCreatorPrice}
                      onChange={(e) => {
                        selectedPrice[i].customCreatorPrice = e.target.value;
                        setSelectedPrice([...selectedPrice]);
                      }}
                      variant="soft"
                      style={{ width: "100%" }}
                    />
                  </Flex>
                  <Flex style={{ width: "40px" }}>
                    <IconButton
                      variant="ghost"
                      onClick={() => {
                        selectedPrice.splice(i, 1);
                        setSelectedPrice([...selectedPrice]);
                      }}
                    >
                      <IconTrash className="icon-accent" />
                    </IconButton>
                  </Flex>
                </Flex>
              );
            })}

            <Button
              disabled={!selectedCreator}
              onClick={() => {
                setSelectedPrice((prev) => [
                  ...prev,
                  { price: null, count: 0 },
                ]);
              }}
            >
              Add Platform
            </Button>
          </Flex>

          <Flex gap="3" mt="4" justify="end">
            <Dialog.Close>
              <Button
                variant="soft"
                color="gray"
                onClick={() => {
                  setSelectedCreator(undefined);
                  setSelectedPrice([]);
                  setCreatorSearchInput("");
                  setCreatorSearchQuery("");
                }}
              >
                Cancel
              </Button>
            </Dialog.Close>
            <Dialog.Close>
              <Button
                onClick={() => add()}
                loading={loading}
                disabled={
                  selectedPrice.length === 0 ||
                  selectedPrice.some((p) => !p.price)
                }
              >
                Save
              </Button>
            </Dialog.Close>
          </Flex>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
